/**
 * `/tutorial` — the complete public tutorial: every path (Bystep builds it · your own CLI · your own
 * computer), plus agent teams, quality gates, MCP, collaboration, publishing, plans and troubleshooting.
 * Landing-styled (`.mk`, light-only, no shadcn primitives). Long-form copy lives in
 * `src/components/tutorial/tutorial-content.{id,en}.ts`; page chrome goes through `m.*`.
 */
import { createFileRoute } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Container, MkAnchor, MkFooter, MkLink, MkNav, Overline } from '#/components/landing/mk'
import { SECTION_IDS, tutorialContent } from '#/components/tutorial'
import { TutChapter, TutToc, TutTocMobile } from '#/components/tutorial/tutorial-ui'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/tutorial')({
  head: () => ({
    meta: [
      { title: m.tut_meta_title() },
      { name: 'description', content: m.tut_meta_desc() },
      { property: 'og:title', content: m.tut_meta_title() },
      { property: 'og:description', content: m.tut_meta_desc() },
      { property: 'og:type', content: 'article' },
      { property: 'og:image', content: 'https://bystep.dev/brand/bystep-og.png' },
    ],
    links: [{ rel: 'canonical', href: 'https://bystep.dev/tutorial' }],
  }),
  component: TutorialPage,
})

function TutorialPage() {
  const c = tutorialContent()
  const toc = SECTION_IDS.map((id) => ({ id, label: c.sections[id].title }))

  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main id="main-content">
        {/* hero */}
        <section className="pt-14 pb-10 lg:pt-20">
          <Container className="flex flex-col items-center gap-5 text-center">
            <Overline>{c.hero.overline}</Overline>
            <h1 className="max-w-[22ch]">{c.hero.title}</h1>
            <p className="mk-lead max-w-[62ch]">{c.hero.lead}</p>
            <p className="mk-tut-meta">{c.hero.meta}</p>
            <div className="mt-1 flex flex-wrap justify-center gap-3">
              <MkAnchor href="#pilih">{m.tut_cta_start()}</MkAnchor>
              <MkAnchor href="#referensi" variant="secondary" arrow>
                {m.tut_cta_reference()}
              </MkAnchor>
            </div>
          </Container>
        </section>

        {/* path picker */}
        <section className="pb-4">
          <Container>
            <div className="mk-tut-paths">
              {c.paths.map((path) => (
                <a key={path.id} href={`#${path.id}`} className="mk-tut-path">
                  <span className="mk-tut-path-badge">{path.badge}</span>
                  <h2>{path.title}</h2>
                  <p className="mk-muted">{path.who}</p>
                  <ul>
                    {path.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                  <span className="mk-tut-path-go">
                    {m.tut_path_open()}
                    <ArrowRight className="size-4" aria-hidden />
                  </span>
                </a>
              ))}
            </div>
            <div className="mk-tut-extras">
              {c.extras.map((extra) => (
                <a key={extra.id} href={`#${extra.id}`} className="mk-tut-extra">
                  <strong>{extra.title}</strong>
                  <span className="mk-muted">{extra.text}</span>
                </a>
              ))}
            </div>
          </Container>
        </section>

        {/* table of contents + chapters */}
        <section className="py-10 lg:py-14">
          <Container>
            <TutTocMobile title={c.tocTitle} items={toc} />
            <div className="mk-tut-layout">
              <aside className="mk-tut-aside">
                <TutToc title={c.tocTitle} items={toc} />
              </aside>
              <div className="mk-tut-content">
                {SECTION_IDS.map((id, i) => (
                  <TutChapter key={id} id={id} index={i + 1} section={c.sections[id]} />
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* closing */}
        <section className="pb-20 lg:pb-24">
          <Container className="flex flex-col items-center gap-5 text-center">
            <h2 className="max-w-[18ch]">{c.closing.title}</h2>
            <p className="mk-lead max-w-[52ch]">{c.closing.lead}</p>
            <div className="mt-1 flex flex-wrap justify-center gap-3">
              <MkLink to="/sign-in" search={{ redirect: '/plan?flow=new' }}>
                {c.closing.primary}
              </MkLink>
              <MkLink to="/templates" search={{ tag: undefined }} variant="secondary" arrow>
                {c.closing.secondary}
              </MkLink>
            </div>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
